import type { ChartConfig } from '@/shared/components/ui/chart'

import {
	CartesianGrid,
	Legend,
	Line,
	LineChart,
	ReferenceLine,
	XAxis,
	YAxis,
} from 'recharts'

import {
	ChartContainer,
	ChartLegendContent,
	ChartTooltip,
	ChartTooltipContent,
} from '@/shared/components/ui/chart'
import { formatNumber, NumberFormats } from '@/shared/tools/locale'

/**
 * Recharts rendering of the Elasticity demand-curve chart.
 *
 * Sibling of {@link ./ChartView} (Highcharts `spline`) kept alongside it for
 * manual comparison during the charting-library migration. Same prop shape
 * (the output of `getElasticityProps`) so it is a drop-in swap for the
 * consumer in `./index.tsx`.
 */

type Props = {
	data: {
		price: number
		sales_items: number
		revenue: number
		gross_profit: number
	}[]
	chartConfig: ChartConfig
	plotLines: {
		value: number
		color: string
		label: string
	}[]
}

type Row = { price: number } & Record<string, number | null>

const TICK_STEP = 0.5

// Mirrors Highcharts `tickInterval: 0.5`: fixed-step ticks across the data
// domain (Highcharts auto-fits the axis extent, so no padding is added here).
const toTicks = (rows: Row[]): number[] => {
	if (!rows.length) return []

	const min = rows[0].price
	const max = rows[rows.length - 1].price
	const ticks: number[] = []

	for (
		let tick = Math.ceil(min / TICK_STEP) * TICK_STEP;
		tick <= max + 1e-9;
		tick += TICK_STEP
	) {
		ticks.push(Number(tick.toFixed(2)))
	}

	return ticks
}

export default function ChartViewRecharts({
	data,
	chartConfig,
	plotLines,
}: Props) {
	const ticks = toTicks(data)

	return (
		<ChartContainer
			config={chartConfig}
			className='h-65 w-full overflow-hidden fade-in'
		>
			<LineChart
				accessibilityLayer={false}
				data={data}
				margin={{ top: 16, right: 8, bottom: 8, left: 8 }}
			>
				<CartesianGrid vertical={false} yAxisId='0' strokeDasharray='3 3' />
				<XAxis
					dataKey='price'
					type='number'
					domain={['dataMin', 'dataMax']}
					ticks={ticks}
					fontSize={10}
					tickLine={false}
					axisLine={false}
				/>
				{/* Sales Items axis */}
				<YAxis
					yAxisId='0'
					width={0}
					hide
					domain={['dataMin', (dataMax) => Math.max(dataMax, 1)]}
				/>
				{/* Revenue axis */}
				<YAxis
					yAxisId='1'
					hide
					orientation='right'
					width={0}
					domain={['dataMin', 'dataMax']}
				/>

				<ChartTooltip
					content={
						<ChartTooltipContent
							labelFormatter={(value) =>
								typeof value === 'string' || typeof value === 'number'
									? formatNumber(Number(value), NumberFormats.Fixed)
									: ''
							}
						/>
					}
				/>
				<Legend content={<ChartLegendContent />} />
				<Line
					key='sales_items'
					yAxisId='0'
					dataKey='sales_items'
					name='Sales Items'
					stroke='var(--color-sales_items)'
					strokeWidth={2}
					dot={false}
					connectNulls
					type='monotone'
				/>
				<Line
					key='revenue'
					yAxisId='1'
					dataKey='revenue'
					name='Revenue'
					stroke='var(--color-revenue)'
					strokeWidth={2}
					dot={false}
					connectNulls
					type='monotone'
				/>
				<Line
					key='gross_profit'
					yAxisId='1'
					dataKey='gross_profit'
					name='Gross Profit'
					stroke='var(--color-gross_profit)'
					strokeWidth={2}
					dot={false}
					connectNulls
					type='monotone'
				/>
				{plotLines.map((line, index) => (
					<ReferenceLine
						key={line.value ?? line.label}
						yAxisId='0'
						x={line.value as number}
						stroke={line.color as string}
						strokeWidth={2}
						label={{
							value: line.label,
							angle: 90,
							position: 'insideRight',
							fill: 'var(--foreground)',
							fontSize: 12,
							fontWeight: 400,
						}}
					/>
				))}
			</LineChart>
		</ChartContainer>
	)
}
